import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePatient } from "@/context/PatientContext";
import { Pill, UsersThree, Check, X, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

// Public axios (no bearer) for the lookup endpoint
const publicApi = axios.create({ baseURL: API });

export default function AcceptInvite() {
  const { code } = useParams();
  const { user } = useAuth();
  const { reload: reloadPatients, setActiveId } = usePatient();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    publicApi.get(`/invites/lookup/${code}`)
      .then((r) => setInvite(r.data))
      .catch((e) => {
        if (e.response?.status === 404) setError("This invite link is invalid or has already been used.");
        else if (e.response?.status === 410) setError("This invite was revoked by the owner.");
        else setError("Couldn't load this invite — please try again.");
      })
      .finally(() => setLoading(false));
  }, [code]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invites/${code}/accept`);
      toast.success(`You're now a ${invite.role} for ${invite.patient_name}`);
      await reloadPatients();
      if (data.patient_id) setActiveId(data.patient_id);
      navigate("/app");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Accept failed");
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/invites/${code}/decline`);
      toast.success("Invite declined");
      navigate("/app");
    } catch {
      toast.error("Couldn't decline — try again");
      setBusy(false);
    }
  };

  const redirectAfterAuth = `/accept/${code}`;

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A2520] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-[#2C5545] flex items-center justify-center">
            <Pill weight="duotone" className="text-white" size={22} />
          </div>
          <div className="font-heading font-extrabold text-xl">EverCara</div>
        </div>

        <div className="bg-white border border-[#EBE8E0] rounded-2xl p-8" data-testid="accept-invite-card">
          {loading ? (
            <div className="space-y-3">
              <div className="h-6 bg-[#EBE8E0]/60 rounded animate-pulse" />
              <div className="h-24 bg-[#EBE8E0]/60 rounded animate-pulse" />
            </div>
          ) : error ? (
            <div className="text-center" data-testid="accept-invite-error">
              <div className="w-14 h-14 rounded-full bg-[#C86A51]/15 flex items-center justify-center mx-auto">
                <Warning size={28} weight="duotone" className="text-[#C86A51]" />
              </div>
              <h1 className="mt-4 font-heading text-2xl font-bold">Invite unavailable</h1>
              <p className="mt-2 text-[#576A62]">{error}</p>
              <Link to="/" className="inline-block mt-6 rounded-full bg-[#2C5545] hover:bg-[#224235] text-white font-semibold px-5 py-2.5">
                Back home
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#EBE8E0] flex items-center justify-center mx-auto">
                  <UsersThree size={28} weight="duotone" className="text-[#2C5545]" />
                </div>
                <div className="mt-4 text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">You've been invited</div>
                <h1 className="mt-2 font-heading text-2xl sm:text-3xl font-bold">
                  Help care for <span className="text-[#2C5545]" data-testid="accept-invite-patient">{invite.patient_name}</span>
                </h1>
                <p className="mt-3 text-[#576A62]">
                  <span className="font-semibold text-[#1A2520]">{invite.owner_name || invite.owner_email}</span> invited you as {invite.role === "editor" ? "an" : "a"}{" "}
                  <span className="font-semibold" data-testid="accept-invite-role">{invite.role}</span>.
                </p>
                {invite.invitee_email && (
                  <p className="mt-1 text-xs text-[#576A62]">Invited: {invite.invitee_email}</p>
                )}
                <div className="mt-4 text-sm text-[#576A62] bg-[#FDFBF7] border border-[#EBE8E0] rounded-xl p-4 text-left">
                  {invite.role === "editor" ? (
                    <>As an <b>editor</b>, you can add and update doctors, medications, appointments, reminders, and more.</>
                  ) : (
                    <>As a <b>viewer</b>, you'll be able to see records but not change them.</>
                  )}
                </div>
              </div>

              {invite.status === "accepted" ? (
                <div className="mt-6 rounded-xl bg-[#3E7B5C]/12 text-[#3E7B5C] text-sm px-4 py-3 flex items-center gap-2" data-testid="accept-invite-already">
                  <Check size={18} weight="bold" /> This invite was already accepted.
                </div>
              ) : user && user !== false ? (
                <div className="mt-8 flex gap-3">
                  <button
                    onClick={accept}
                    disabled={busy}
                    data-testid="accept-invite-accept"
                    className="flex-1 h-12 rounded-full bg-[#2C5545] hover:bg-[#224235] disabled:opacity-60 text-white font-semibold inline-flex items-center justify-center gap-2"
                  >
                    <Check size={18} weight="bold" /> Accept invite
                  </button>
                  <button
                    onClick={decline}
                    disabled={busy}
                    data-testid="accept-invite-decline"
                    className="rounded-full border border-[#EBE8E0] text-[#576A62] hover:bg-[#EBE8E0]/60 font-semibold px-5"
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <div className="mt-8 space-y-3">
                  <p className="text-sm text-[#576A62] text-center">
                    Sign in or create a EverCara account to accept.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to={`/register?redirect=${encodeURIComponent(redirectAfterAuth)}${invite.invitee_email ? `&email=${encodeURIComponent(invite.invitee_email)}` : ""}`}
                      data-testid="accept-invite-register"
                      className="flex-1 h-12 rounded-full bg-[#2C5545] hover:bg-[#224235] text-white font-semibold inline-flex items-center justify-center"
                    >
                      Create account
                    </Link>
                    <Link
                      to={`/login?redirect=${encodeURIComponent(redirectAfterAuth)}`}
                      data-testid="accept-invite-login"
                      className="flex-1 h-12 rounded-full border border-[#2C5545] text-[#2C5545] hover:bg-[#EBE8E0] font-semibold inline-flex items-center justify-center"
                    >
                      Sign in
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
